import { stripHtmlFromPlainText } from "./cv-validation.runtime.js";
import { normalizeExperienceLocationType } from "./experience-location-type.runtime.js";

/** Some PDF text layers flatten the entire personal block onto one line.
 * Only remove value-validated fields after three DISTINCT personal labels,
 * including a birth date, corroborate the block. Adjacent career fields are
 * boundaries, not part of the personal value. Ambiguous prose stays intact. */
function stripInlinePersonalFields(value: string): string {
  const labels = [
    ...value.matchAll(
      /\b(name|date\s+of\s+birth|d\.?o\.?b|birth\s*date|marital\s+status|gender|nationality|citizenship|mobile(?:\s+no\.?)?|phone|e-?mail|address|contact\s+details|driving\s+licen[cs]e|career\s+objective|objective|professional\s+summary|job\s+title|experience|projects?)\s*:/giu,
    ),
  ];
  const fields = labels.map((match, index) => {
    const start = match.index ?? 0;
    const end = labels[index + 1]?.index ?? value.length;
    return {
      start,
      end,
      label: match[1].toLowerCase().replace(/[.\s-]/g, ""),
      text: value.slice(start + match[0].length, end).trim(),
      bounded: index + 1 < labels.length,
    };
  });
  const valid = fields.filter(({ label, text, bounded }) => {
    if (/^(dateofbirth|dob|birthdate)$/.test(label))
      return /^\d{1,2}[./-]\d{1,2}[./-](?:19|20)\d{2}$/.test(text);
    if (label === "maritalstatus")
      return /^(married|single|divorced|widowed)$/i.test(text);
    if (label === "gender") return /^(male|female)$/i.test(text);
    if (label === "name") return bounded && /^[\p{L} .'-]{2,100}$/u.test(text);
    if (/^(nationality|citizenship)$/.test(label))
      return bounded && /^[\p{L} '-]{2,60}$/u.test(text);
    if (/^(mobile(?:no)?|phone)$/.test(label))
      return /^\+?[\d ()-]{9,22}$/.test(text);
    if (label === "email") return /^[\w.+%-]+@[\w.-]+\.[a-z]{2,}$/i.test(text);
    // Only the observed bounded postal-box shape is unambiguous enough.
    // A general or terminal address may contain unlabeled career facts.
    if (label === "address")
      return (
        bounded &&
        text.length > 2 &&
        text.length <= 150 &&
        /\bP\.?\s*O\.?\s*Box\s*:\s*\d+/iu.test(text) &&
        !/[.!?]\s+[A-Z][a-z]{2,}/u.test(
          text.replace(/\bP\.?\s*O\.?\s*Box\s*:\s*\d+/giu, ""),
        )
      );
    return false;
  });
  if (
    new Set(valid.map(({ label }) => label)).size < 3 ||
    !valid.some(({ label }) => /^(dateofbirth|dob|birthdate)$/.test(label))
  )
    return value;
  let result = value;
  // An empty Contact Details label is decoration, but cannot corroborate the
  // block itself. Keep it if it has any unclassified content.
  const removable = [
    ...valid,
    ...fields.filter(({ label, text }) => label === "contactdetails" && !text),
  ].sort((a, b) => b.start - a.start);
  for (const { start, end } of removable)
    result = result.slice(0, start) + result.slice(end);
  return result.trim();
}

/** Imported multi-column CVs can append a personal-details block to a project
 * or role. Remove only standalone labelled fields in a corroborated block;
 * ordinary prose about identity documents and isolated credentials survive. */
export function stripCvPersonalDetailsBlock(value: string): string {
  value = stripInlinePersonalFields(value);
  const segments = [...value.matchAll(/[^\n|•●▪◦‣\uf0a7\uf0b7]+/gu)];
  const fields = segments.filter(([segment]) =>
    /^(?:(?:nationality|citizenship)\s*:\s*[\p{L} .'-]{2,60}|gender\s*:\s*(?:male|female)|marital\s+status\s*:\s*(?:married|single|divorced|widowed)|(?:date\s+of\s+birth|d\.?o\.?b|birth\s*date|validity|passport\s+expiry)\s*:\s*\d[\d ./-]{5,14}|passport\s*(?:no\.?|number|#)?\s*:\s*(?=[a-z0-9 -]*\d)[a-z0-9 -]{5,20})[.]?$/iu.test(
      segment.trim(),
    ),
  );
  if (
    fields.length < 3 ||
    !fields.some(([segment]) =>
      /^\s*(?:passport\s*(?:no\.?|number|#)?|date\s+of\s+birth|d\.?o\.?b|birth\s*date)\s*:/iu.test(
        segment,
      ),
    )
  )
    return value;

  let result = value;
  for (const match of fields.reverse()) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    // Consume one separator with the removed field, retaining every other
    // segment verbatim (including adjacent project facts and languages).
    result = result.slice(0, Math.max(0, start - 1)) + result.slice(end);
  }
  return result.replace(/^[\s|•●▪◦‣]+|[\s|•●▪◦‣]+$/gu, "");
}

/** Presentation controls copied from HTML/PDF are not CV content. Keep Arabic
 * letters, combining marks, ZWJ and ZWNJ: those affect the actual spelling. */
export function normalizeCvText(value: string): string {
  return stripCvPersonalDetailsBlock(
    stripHtmlFromPlainText(value.replace(/<li(?:\s[^>]*)?>/gi, "\n• "))
      // Strip direction wrappers first so they cannot hide a list boundary
      // until a later normalization pass in another renderer.
      .replace(/[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, "")
      // Common Word and icon-font list characters live in the private-use area.
      // With a normal CV font they become unrelated letters or missing glyphs.
      // Only translate known list glyphs at a word boundary; never erase the
      // whole private-use range (a document may use it for authored content).
      .replace(
        /(^|\s)[\uf0a7\uf0b7\uf0fc\uf06c\uf0b6\uf0d8\uf0a4\uf00c](?=\s|\p{L}|$)(\p{L}?)/gu,
        (_match, boundary: string, attachedLetter: string) =>
          `${boundary}•${attachedLetter ? ` ${attachedLetter}` : ""}`,
      )
      .replace(/\r\n?/g, "\n")
      .trim(),
  );
}

/** Delimiters inside a parenthetical skill belong to that skill, not a new
 * item. Keep complete source labels; presentation budgets apply to AI additions. */
export function splitCvSkillList(value: string): string[] {
  const parts: string[] = [];
  let current = "";
  let depth = 0;
  for (const character of normalizeCvText(value)) {
    if ("([{".includes(character)) depth += 1;
    if (")]}".includes(character)) depth = Math.max(0, depth - 1);
    if (depth === 0 && "|;,•·\n".includes(character)) {
      if (current.trim()) parts.push(current.trim());
      current = "";
    } else current += character;
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}

/** Split explicit copied list markers, never ordinary hyphenated names or
 * comma-delimited parentheticals. A dash inside prose is not a list marker. */
function splitDecoratedSkills(value: string): string[] {
  const text = normalizeCvText(value);
  const markedDashList = /^[-–—]\s+/.test(text);
  const tabbedDashList = (text.match(/\s[-–—]\t+/g) ?? []).length >= 2;
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < text.length; index++) {
    const character = text[index];
    if ("([{".includes(character)) depth++;
    if (")]}".includes(character)) depth = Math.max(0, depth - 1);
    if (depth > 0) continue;
    const graphical = /[•●▪◦‣▸➢■□\uf0b7\uf020]/u.test(character);
    const dash =
      /[-–—]/u.test(character) &&
      (index === 0 || /\s/u.test(text[index - 1])) &&
      /\s/u.test(text[index + 1] ?? "") &&
      (markedDashList || (tabbedDashList && text[index + 1] === "\t"));
    if (graphical || dash) {
      const part = text.slice(start, index).trim();
      if (part) parts.push(part);
      start = index + 1;
    }
  }
  const final = text.slice(start).trim();
  if (final) parts.push(final);
  return parts;
}

/** A complete stock CV declaration is document furniture, not a capability.
 * Keep this exact: professional work involving declarations is real content. */
export function isCvDeclarationBoilerplate(value: string): boolean {
  return /^i\s+(?:hereby\s+)?(?:declare|certify)\s+that\s+(?:all\s+)?(?:the\s+)?(?:above(?:[- ]mentioned)?\s+(?:information|details)|(?:information|details)\s+(?:given|provided)\s+above)\s+(?:is|are)\s+(?:correct\s+and\s+true|true\s+and\s+correct|true|correct)(?:\s+to\s+the\s+best\s+of\s+my\s+knowledge(?:\s+and\s+belief)?)?[.!]?$/i.test(
    normalizeCvText(value),
  );
}

/** An imported builder footer is only identifiable with both its brand URL
 * and its attribution. Never erase arbitrary URLs or private-use characters:
 * those can represent authored content in a different source font. */
export function stripCvTemplateFooterLines(items: readonly string[]): string[] {
  const brandUrl = /^(?:https?:\/\/)?(?:www\.)?enhancv\.com\/?$/iu;
  const attribution = /^[\s\ue06d]*powered\s+by(?:\s+enhancv(?:\.com)?)?$/iu;
  const lines = items.map(normalizeCvText);
  if (
    !lines.some((line) => brandUrl.test(line)) ||
    !lines.some((line) => attribution.test(line))
  )
    return [...items];
  return items.filter((_item, index) => {
    const line = lines[index];
    const footerIcons =
      /[\ue069\ue092]/u.test(line) && /^[E\s\ue069\ue092]+$/u.test(line);
    return !brandUrl.test(line) && !attribution.test(line) && !footerIcons;
  });
}

/** Remove copied list decoration, never rewrite a skill or split its words.
 * Dedupe only complete labels after case/whitespace normalization. In
 * particular, C++, C#, .NET, ISO numbers and parenthetical lists are content. */
export function normalizeCvSkillItems(items: readonly string[]): string[] {
  const seen = new Set<string>();
  const parts = stripCvTemplateFooterLines(items).flatMap(splitDecoratedSkills);
  const labelKey = (value: string) =>
    value.replace(/\s+/gu, " ").replace(/\.$/u, "").trim().toLowerCase();
  // Require a list sequence. A standalone version/standard starting with a
  // number is content, not permission to strip its prefix.
  const numbered = new Set(
    parts.map((part) => part.match(/^(\d{1,2})[.)]\s+/u)?.[1]),
  );
  const hasNumberedList = numbered.has("1") && numbered.has("2");
  const originalLabels = new Set(parts.map(labelKey));
  return parts.flatMap((raw) => {
    const undecorated = hasNumberedList
      ? raw.replace(/^\d{1,2}[.)]\s+/u, "")
      : raw;
    // A copied unspaced dash is only decoration when the same full label is
    // also present without it. Preserve CLI switches and hyphenated labels.
    const matchedDashCopy =
      /^-\p{L}/u.test(undecorated) &&
      originalLabels.has(labelKey(undecorated.slice(1)));
    let item = normalizeCvText(
      matchedDashCopy ? undecorated.slice(1) : undecorated,
    )
      .replace(/^(?:[•●▪◦‣▸➢■□\uf0b7\uf020]\s*|[-*]\s+)+/u, "")
      .trim();
    // Sentence punctuation copied with a skill would render as "Support.,".
    // Keep dotted abbreviations and technical labels (C++, .NET, Node.js).
    if (!/^[.-]/u.test(item)) item = item.replace(/([a-z]{4,})\.$/u, "$1");
    if (!item || /^[\s•●▪◦‣▸➢■□\uf0b7\uf020*-]+$/u.test(item)) return [];
    // These complete entries are extraction furniture/contact fields, never
    // professional skill labels. Do not remove prose merely containing one.
    if (
      isCvDeclarationBoilerplate(item) ||
      /^(?:page\s+)?\d+\s+(?:of|\/)\s+\d+$/i.test(item) ||
      /^(?:e-?mail\s*:\s*)?[\w.+%-]+@[\w.-]+\.[a-z]{2,}$/i.test(item) ||
      (/^(?:(?:phone|mobile|tel(?:ephone)?|contact)\s*[:.]?\s*)?\+?\d[\d\s().-]{6,}\d$/i.test(
        item,
      ) &&
        item.replace(/\D/g, "").length >= 9)
    )
      return [];
    const key = labelKey(item);
    if (seen.has(key)) return [];
    seen.add(key);
    return [item];
  });
}

/** A repeated label is presentation duplication, not a second achievement.
 * Preserve all distinct wording and only collapse an exact label prefix. */
export function normalizeCvHighlightContent<
  T extends { title?: string | null; desc?: string | null },
>(value: T): T {
  if (!value.title?.trim() || !value.desc?.trim()) return value;
  const title = value.title
    .trim()
    .replace(/[:：]+\s*$/u, "")
    .trim();
  let desc = value.desc.trim();
  if (!title) return value;
  const prefix = desc.slice(0, title.length);
  if (
    prefix.toLowerCase() === title.toLowerCase() &&
    /^\s*[:：]\s*/u.test(desc.slice(title.length))
  ) {
    desc = desc.slice(title.length).replace(/^\s*[:：]\s*/u, "");
  }
  return { ...value, title, desc };
}

type CvSkillContent = {
  skills?: string[] | null;
  skillGroups?: Array<{ category: string; items: string[] }> | null;
  highlights?: Array<{ title?: string | null; desc?: string | null }> | null;
};

/** Keep explicit group membership and the flat list independent. Merging
 * them on read could restore a skill the user deliberately removed. */
function normalizeCvSkills<T extends CvSkillContent>(value: T): T {
  const groupedLabels = new Set<string>();
  const groups = value.skillGroups?.map((group) => ({
    ...group,
    // Normalization can make a decorated source label equal to a suggestion
    // in another group. Keep its first authored placement.
    items: normalizeCvSkillItems(group.items).filter((item) => {
      const key = item.replace(/\s+/gu, " ").toLowerCase();
      if (groupedLabels.has(key)) return false;
      groupedLabels.add(key);
      return true;
    }),
  }));
  return {
    ...value,
    ...(value.skills ? { skills: normalizeCvSkillItems(value.skills) } : {}),
    ...(value.highlights
      ? { highlights: value.highlights.map(normalizeCvHighlightContent) }
      : {}),
    ...(groups
      ? {
          // Do not leave a blank category after collapsing all its duplicates.
          // Preserve an entirely empty explicit selection: exporters otherwise
          // fall back to the flat list and can restore skills the owner hid.
          skillGroups: groups.some((group) => group.items.length > 0)
            ? groups.filter((group) => group.items.length > 0)
            : groups,
        }
      : {}),
  };
}

/** Copy, never mutate, persisted documents and incoming structured CV data. */
export function normalizeCvTextFields<T>(value: T): T {
  if (typeof value === "string") return normalizeCvText(value) as T;
  if (Array.isArray(value)) return value.map(normalizeCvTextFields) as T;
  if (value && typeof value === "object" && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        normalizeCvTextFields(entry),
      ]),
    ) as T;
  }
  return value;
}

function splitMarkedList(value: string): {
  narrative: string;
  bullets: string[];
} {
  const text = normalizeCvText(value.replace(/<li(?:\s[^>]*)?>/gi, "\n• "));
  const marker = /(?:^|\n)[ \t]*[•●▪◦‣*-][ \t]+/;
  // A flattened list can lose its first marker. Require multiple subsequent
  // graphical markers; never split prose on punctuation, commas or hyphens.
  const inline = text.match(/\s+[•●▪◦‣]\s+(?=[^\s\p{Ll}])/gu) ?? [];
  if (inline.length >= 2 && !marker.test(text)) {
    return {
      narrative: "",
      bullets: text.split(/\s+[•●▪◦‣]\s+(?=[^\s\p{Ll}])/u).filter(Boolean),
    };
  }
  if (!marker.test(text)) return { narrative: text, bullets: [] };
  const expanded = text.replace(/\s+([•●▪◦‣])\s+(?=[^\s\p{Ll}])/gu, "\n$1 ");
  const parts = expanded.split(/(?:^|\n)[ \t]*[•●▪◦‣*-][ \t]+/);
  return {
    narrative: parts.shift()?.trim() ?? "",
    bullets: parts.map((p) => p.trim()).filter(Boolean),
  };
}

export function normalizeCvExperienceContent(
  description?: string | null,
  achievements?: readonly string[] | null,
): { overview: string; bullets: string[] } {
  const parsed = splitMarkedList(description ?? "");
  const bullets = [
    ...parsed.bullets,
    ...(achievements ?? []).flatMap((value) => {
      const item = splitMarkedList(value);
      return [item.narrative, ...item.bullets].filter(Boolean);
    }),
  ];
  // Only exact duplicate items are collapsed, without rewriting their words.
  return { overview: parsed.narrative, bullets: [...new Set(bullets)] };
}

type ExperienceMetadata = {
  location?: string | null;
  locationType?: string | null;
  employmentType?: string | null;
};
const EMPLOYMENT_TYPES =
  /^(full[ -]time|part[ -]time|contract|freelance|internship|volunteer|apprenticeship\/trainee|temporary|self[ -]employed)$/i;

/** Recover only explicit, delimited metadata; never infer employment status. */
export function normalizeCvExperienceMetadata<T extends ExperienceMetadata>(
  value: T,
): T {
  let locationType = value.locationType
    ? normalizeCvText(value.locationType)
    : value.locationType;
  let employmentType = value.employmentType
    ? normalizeCvText(value.employmentType)
    : value.employmentType;
  const location =
    value.location == null
      ? value.location
      : normalizeCvText(value.location)
          .split(/\s*[|•]\s*/)
          .filter((part) => {
            const arrangement = normalizeExperienceLocationType(part);
            if (
              arrangement &&
              (!locationType ||
                arrangement.toLowerCase() === locationType.toLowerCase())
            ) {
              locationType ||= arrangement;
              return false;
            }
            const matchesEmployment =
              employmentType &&
              part.toLowerCase().replace(/ /g, "-") ===
                employmentType.toLowerCase().replace(/ /g, "-");
            if (
              matchesEmployment ||
              (!employmentType && EMPLOYMENT_TYPES.test(part))
            ) {
              employmentType ||= part;
              return false;
            }
            return true;
          })
          .join(" | ");
  return { ...value, location, locationType, employmentType };
}

type CvExperienceContent = ExperienceMetadata & {
  overview?: string | null;
  bullets?: readonly string[] | null;
};

/** One content boundary for previews and every export of existing CVs. */
export function normalizeCvDocumentContent<
  T extends CvSkillContent & { experiences: CvExperienceContent[] },
>(document: T): T {
  const clean = normalizeCvSkills(normalizeCvTextFields(document));
  return {
    ...clean,
    experiences: clean.experiences.map((experience) => ({
      ...normalizeCvExperienceMetadata(experience),
      ...normalizeCvExperienceContent(experience.overview, experience.bullets),
    })),
  };
}

/** The editor/native renderer uses singular `experience` with the same content. */
export function normalizeCvProfileContent<
  T extends CvSkillContent & { experience: CvExperienceContent[] },
>(profile: T): T {
  const clean = normalizeCvSkills(normalizeCvTextFields(profile));
  return {
    ...clean,
    experience: clean.experience.map((experience) => ({
      ...normalizeCvExperienceMetadata(experience),
      ...normalizeCvExperienceContent(experience.overview, experience.bullets),
    })),
  };
}
